import IndicatorSection from '../../components/IndicatorSection'
import type {
  Municipality,
  County,
  BBSRRegionInfo,
  MunicipalityBBSRMappings,
  CountyBBSRMappings,
} from '../../types/api'

/**
 * Configuration for all BBSR region types.
 *
 * Each entry maps a key on the bbsr_mappings object to its display label
 * and the entity type expected by the IndicatorSection component.
 *
 * "source" controls where the region is available:
 *   - 'both'       -> municipality and county mappings
 *   - 'municipality' -> municipality mappings only (e.g. Mittelbereich)
 *   - 'county'     -> county mappings only (e.g. IHK-Bezirk)
 */
const BBSR_REGIONS: {
  key: string
  label: string
  entityType: string
  source: 'both' | 'municipality' | 'county'
}[] = [
  { key: 'metropolregion', label: 'Metropolregion', entityType: 'metropolitan_region', source: 'both' },
  { key: 'grossstadtregion', label: 'Großstadtregion', entityType: 'major_city_region', source: 'both' },
  { key: 'raumordnungsregion', label: 'Raumordnungsregion', entityType: 'spatial_planning_region', source: 'both' },
  { key: 'arbeitsmarktregion', label: 'Arbeitsmarktregion', entityType: 'labor_market_region', source: 'both' },
  { key: 'kreisregion', label: 'Kreisregion', entityType: 'county_region', source: 'both' },
  { key: 'mittelbereich', label: 'Mittelbereich', entityType: 'bbsr_middle_area', source: 'municipality' },
  { key: 'ihk_bezirk', label: 'IHK-Bezirk', entityType: 'ihk_district', source: 'county' },
]

interface BBSRTabProps {
  municipality: Municipality
  countyData: County | undefined
}

/**
 * Resolve a BBSR region from either municipality or county mappings
 * depending on the region's source configuration.
 */
function resolveRegion(
  config: (typeof BBSR_REGIONS)[number],
  municipalityMappings: MunicipalityBBSRMappings | null | undefined,
  countyMappings: CountyBBSRMappings | null | undefined,
): BBSRRegionInfo | null | undefined {
  const key = config.key as keyof MunicipalityBBSRMappings & keyof CountyBBSRMappings

  if (config.source === 'municipality') {
    return municipalityMappings?.[key as keyof MunicipalityBBSRMappings] as BBSRRegionInfo | null | undefined
  }
  if (config.source === 'county') {
    return countyMappings?.[key as keyof CountyBBSRMappings] as BBSRRegionInfo | null | undefined
  }
  // 'both': prefer municipality, fall back to county
  return (
    (municipalityMappings?.[key as keyof MunicipalityBBSRMappings] as BBSRRegionInfo | null | undefined) ||
    (countyMappings?.[key as keyof CountyBBSRMappings] as BBSRRegionInfo | null | undefined)
  )
}

export default function BBSRTab({ municipality, countyData }: BBSRTabProps) {
  const municipalityMappings = municipality?.bbsr_mappings
  const countyMappings = countyData?.bbsr_mappings

  if (!municipalityMappings && !countyMappings) {
    return (
      <div className="status-error text-center">
        <p>Keine BBSR-Raumgliederungsdaten verfügbar</p>
      </div>
    )
  }

  // Pre-resolve all regions so we can filter to only those that exist
  const resolvedRegions = BBSR_REGIONS.map((config) => ({
    config,
    region: resolveRegion(config, municipalityMappings, countyMappings),
  })).filter(({ region }) => !!region) as {
    config: (typeof BBSR_REGIONS)[number]
    region: BBSRRegionInfo
  }[]

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <div className="card-dark">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-text-inverse">BBSR-Raumgliederungen</h3>
          <span className="text-xs text-primary bg-text-inverse/10 px-2 py-1 rounded-gut8">
            Gebietsstand {municipalityMappings?.reference_year || countyMappings?.reference_year}
          </span>
        </div>
        <p className="text-text-inverse/70 text-sm mb-6">
          Die BBSR-Raumgliederungen ordnen {municipality?.name} verschiedenen statistischen Regionen zu,
          die für Raumbeobachtung, Regionalplanung und Politikberatung genutzt werden.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {resolvedRegions.map(({ config, region }) => (
            <div key={config.key} className="bg-text-inverse/5 p-3 rounded-gut8">
              <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                {config.label}
              </h4>
              <div className="text-sm font-semibold text-text-inverse">
                {region.name}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Indicator Sections for each BBSR Region */}
      {resolvedRegions.map(({ config, region }) => (
        <IndicatorSection
          key={config.key}
          entityType={config.entityType as any}
          ags={region.code}
          title={`${config.label} ${region.name}`}
        />
      ))}
    </div>
  )
}
